import PropTypes from "prop-types"
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { Controller, useFieldArray, useForm } from "react-hook-form"
import { buildReactivateProductAction } from "src/actions/products"
import { useMarina } from "src/contexts/MarinaContext"
import AddLineItemButtons from "src/main/Service/Manage/shared/AddLineItemButtons"
import InactiveProductsNotice from "src/main/Service/Manage/shared/InactiveProductsNotice"
import JobLineItemForm from "src/main/Service/Manage/shared/JobLineItemForm"
import { buildJobLineItemColumns } from "src/main/Service/Manage/shared/jobLineItemColumns"
import { useReactivateLineItemProduct } from "src/main/Service/Manage/shared/use_reactivate_line_item_product"
import { v4 as uuidv4 } from "uuid"

import Button from "src/components/Button"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import InlineNotice from "src/components/InlineNotice"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { computeSubtotal } from "../shared/helpers"
import JobFormFields from "./JobFormFields"

const buildItemFromTemplateLineItem = (li, boatLengthFeet) => {
  const product = li.product
  const isByLength = product?.pricingStructure === "by_length"
  const quantity = isByLength ? boatLengthFeet ?? 0 : Number(li.quantity)
  const pricePerUnit = product?.pricePerUnit ?? 0
  return {
    clientId: uuidv4(),
    productId: li.productId,
    pricePerUnit,
    quantity,
    notes: "",
    product,
    subtotal: computeSubtotal({
      pricePerUnit,
      quantity,
      pricePrecision: product?.pricePrecision,
    }),
  }
}

const baselineSnapshot = (template, items) => ({
  name: template?.name ?? "",
  description: template?.description ?? "",
  items: items.map(({ productId, pricePerUnit, quantity, notes }) => ({
    productId,
    pricePerUnit,
    quantity,
    notes,
  })),
})

const validateLineItems = (items) =>
  items.every((item) => Number(item.quantity) > 0) ||
  "Each line item needs a quantity. Update or remove rows showing 0."

const JobFillStep = forwardRef(
  (
    {
      template,
      boatLengthFeet,
      presentmentCurrency,
      onChangeTemplate,
      onSubmit,
      onDirtyChange,
      onFormOpenChange,
      onBlockedChange,
    },
    ref
  ) => {
    const initialItems = useMemo(
      () =>
        template
          ? template.lineItems.map((li) =>
              buildItemFromTemplateLineItem(li, boatLengthFeet)
            )
          : [],
      // Initial state is derived once at mount; remount via key handles template changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const [formState, setFormState] = useState({ mode: "closed" })
    const baselineRef = useRef(baselineSnapshot(template, initialItems))

    const {
      control,
      handleSubmit,
      watch,
      formState: { errors },
    } = useForm({
      defaultValues: {
        name: template?.name ?? "",
        description: template?.description ?? "",
        lineItems: initialItems,
        saveAsTemplate: false,
      },
    })

    const {
      fields: pendingItems,
      append,
      remove,
      update,
      replace,
    } = useFieldArray({
      control,
      name: "lineItems",
      keyName: "_rhfId",
      rules: { validate: validateLineItems },
    })

    const { marinaSlug } = useMarina()

    const inactiveCount = useMemo(
      () =>
        pendingItems.filter((item) => item.product?.active === false).length,
      [pendingItems]
    )
    const hasInactiveItems = inactiveCount > 0

    useEffect(() => {
      onBlockedChange?.(hasInactiveItems)
    }, [hasInactiveItems, onBlockedChange])

    const markProductActive = useCallback(
      (productId) => {
        replace(
          pendingItems.map((item) =>
            item.productId === productId && item.product?.active === false
              ? { ...item, product: { ...item.product, active: true } }
              : item
          )
        )
      },
      [pendingItems, replace]
    )

    const { reactivate, reactivatingProductId } = useReactivateLineItemProduct({
      onReactivated: markProductActive,
    })

    const nameValue = watch("name")
    const descriptionValue = watch("description")

    const isFormOpen = formState.mode !== "closed"

    const isDirty = useMemo(() => {
      if (isFormOpen) return true
      if ((nameValue ?? "") !== baselineRef.current.name) return true
      if ((descriptionValue ?? "") !== baselineRef.current.description)
        return true
      const baseItems = baselineRef.current.items
      if (pendingItems.length !== baseItems.length) return true
      return pendingItems.some((item, idx) => {
        const base = baseItems[idx]
        return (
          !base ||
          item.productId !== base.productId ||
          item.pricePerUnit !== base.pricePerUnit ||
          item.quantity !== base.quantity ||
          (item.notes ?? "") !== (base.notes ?? "")
        )
      })
    }, [isFormOpen, nameValue, descriptionValue, pendingItems])

    useEffect(() => {
      onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    useEffect(() => {
      onFormOpenChange?.(isFormOpen)
    }, [isFormOpen, onFormOpenChange])

    const submit = useCallback(
      () =>
        handleSubmit(({ name, description, lineItems, saveAsTemplate }) =>
          onSubmit({
            name,
            description,
            lineItems: lineItems.map(
              ({ productId, pricePerUnit, quantity, notes, product }) => ({
                productId,
                pricePerUnit,
                quantity,
                notes,
                productType: product?.productType,
              })
            ),
            saveAsTemplate,
          })
        )(),
      [handleSubmit, onSubmit]
    )

    useImperativeHandle(ref, () => ({ submit }), [submit])

    const columns = useMemo(
      () =>
        buildJobLineItemColumns({
          presentmentCurrency,
          onEditNote: isFormOpen
            ? () => {}
            : (row) => setFormState({ mode: "edit", clientId: row.id }),
        }),
      [presentmentCurrency, isFormOpen]
    )

    const tableData = useMemo(
      () =>
        pendingItems.map((item) => ({
          id: item.clientId,
          productName: item.product?.name,
          productType: item.product?.productType,
          product: item.product,
          pricePerUnit: item.pricePerUnit,
          quantity: item.quantity,
          subtotal: item.subtotal,
          notes: item.notes,
        })),
      [pendingItems]
    )

    const tableSubtotal = useMemo(
      () => pendingItems.reduce((sum, item) => sum + (item.subtotal ?? 0), 0),
      [pendingItems]
    )

    const tableActions = useMemo(
      () => [
        {
          label: "Edit",
          icon: "pen-to-square",
          iconStyle: "solid",
          disabled: () => isFormOpen,
          onClickSingle: (row) =>
            setFormState({ mode: "edit", clientId: row.id }),
        },
        buildReactivateProductAction({
          canReactivate: !!template?.canReactivateProducts,
          onReactivate: reactivate,
          reactivatingProductId,
          disabled: () => isFormOpen,
        }),
        {
          label: "Remove",
          icon: "trash-can",
          iconStyle: "solid",
          danger: true,
          disabled: () => isFormOpen,
          onClickSingle: (row) => {
            const idx = pendingItems.findIndex(
              (item) => item.clientId === row.id
            )
            if (idx !== -1) remove(idx)
          },
        },
      ],
      [
        isFormOpen,
        pendingItems,
        remove,
        template,
        reactivate,
        reactivatingProductId,
      ]
    )

    const editingItem =
      formState.mode === "edit"
        ? pendingItems.find((item) => item.clientId === formState.clientId)
        : null

    const formInitialValues = editingItem
      ? {
          product: editingItem.product,
          productId: editingItem.productId,
          pricePerUnit: editingItem.pricePerUnit,
          quantity: editingItem.quantity,
          notes: editingItem.notes,
        }
      : undefined

    const formAvailability = (() => {
      if (formState.mode === "add") return formState.availability
      if (editingItem?.product?.productType === "labor") return "service_labor"
      return "service_parts"
    })()

    const handleFormSubmit = useCallback(
      ({ productId, pricePerUnit, quantity, notes, product }) => {
        const subtotal = computeSubtotal({
          pricePerUnit,
          quantity,
          pricePrecision: product.pricePrecision,
        })

        if (formState.mode === "edit") {
          const idx = pendingItems.findIndex(
            (item) => item.clientId === formState.clientId
          )
          if (idx !== -1) {
            update(idx, {
              ...pendingItems[idx],
              productId,
              pricePerUnit,
              quantity,
              notes,
              product,
              subtotal,
            })
          }
        } else {
          append({
            clientId: uuidv4(),
            productId,
            pricePerUnit,
            quantity,
            notes,
            product,
            subtotal,
          })
        }

        setFormState({ mode: "closed" })
      },
      [formState, pendingItems, append, update]
    )

    const handleFormCancel = useCallback(
      () => setFormState({ mode: "closed" }),
      []
    )

    return (
      <>
        {template && (
          <InlineNotice
            variant="info"
            icon="fa-clipboard-list"
            iconStyle="regular"
          >
            <div className="flex items-center justify-between gap-3">
              <span>
                Pre-filled from template: <strong>{template.name}</strong>
              </span>
              <Button variant="link" onClick={onChangeTemplate}>
                Change
              </Button>
            </div>
          </InlineNotice>
        )}

        <JobFormFields
          control={control}
          errors={errors}
          namePlaceholder="e.g., Bottom Paint, Engine Service"
          descriptionPlaceholder="Optional description of the work to be done"
        />

        {hasInactiveItems && (
          <InactiveProductsNotice
            count={inactiveCount}
            contextLabel="job"
            resolutionTail="before adding this job."
            canReactivate={!!template?.canReactivateProducts}
            marinaSlug={marinaSlug}
          />
        )}

        <div>
          <Form.Label>Line items</Form.Label>
          {pendingItems.length === 0 ? (
            <div className="rounded-lg border bg-gray-100 p-4 text-center text-sm font-semibold">
              No line items yet. Add labor or parts below.
            </div>
          ) : (
            <DataTableV2
              name="create-job-pending-items"
              columns={columns}
              data={tableData}
              actions={tableActions}
              hideSettings
              footerRow={{
                productName: "Subtotal",
                subtotal: formatCurrencyFromMinorUnits({
                  amount: tableSubtotal,
                  currency: presentmentCurrency,
                  precision: 2,
                }),
              }}
            />
          )}
          {errors.lineItems?.root?.message && (
            <Form.Error>{errors.lineItems.root.message}</Form.Error>
          )}
        </div>

        {isFormOpen && (
          <div className="rounded-lg border border-blue-200 p-4">
            <JobLineItemForm
              availability={formAvailability}
              boatLengthFeet={boatLengthFeet}
              initialValues={formInitialValues}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              submitLabel={formState.mode === "edit" ? "Update" : "Add"}
            />
          </div>
        )}

        <AddLineItemButtons
          disabled={isFormOpen}
          onAddLabor={() =>
            setFormState({ mode: "add", availability: "service_labor" })
          }
          onAddParts={() =>
            setFormState({ mode: "add", availability: "service_parts" })
          }
        />

        {!template && (
          <Controller
            name="saveAsTemplate"
            control={control}
            render={({ field }) => (
              <div
                data-testid="save-as-template-card"
                className="cursor-pointer rounded-lg border border-blue-100 bg-blue-50 p-4"
                onClick={(e) => {
                  if (e.target.tagName === "INPUT" || e.target.closest("label"))
                    return
                  field.onChange(!field.value)
                }}
              >
                <Form.Checkbox
                  name="saveAsTemplate"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  labelClassName="m-0 ml-2"
                >
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">
                      Save as job template
                    </span>
                    <span className="text-sm text-gray-600">
                      Save this job configuration for reuse on future work
                      orders.
                    </span>
                  </div>
                </Form.Checkbox>
              </div>
            )}
          />
        )}
      </>
    )
  }
)

JobFillStep.displayName = "JobFillStep"

JobFillStep.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    lineItems: PropTypes.array,
    canReactivateProducts: PropTypes.bool,
  }),
  boatLengthFeet: PropTypes.number,
  presentmentCurrency: PropTypes.string.isRequired,
  onChangeTemplate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  onFormOpenChange: PropTypes.func,
  onBlockedChange: PropTypes.func,
}

export default JobFillStep
